import React from 'react';
import { X, PencilSimple, ArrowsClockwise, FilePdf, PaperPlaneTilt } from '@phosphor-icons/react';

/**
 * Read-only quote/avenant consultation modal.
 * Shows the quote header + lines table, with primary actions on the side.
 *
 * Props:
 * - quote: full quote object (with lines, total_*, etc.)
 * - onClose: () => void
 * - onEdit: () => void
 * - onConvert: () => void   (only shown for quotes that can still be invoiced)
 * - onPdf: () => void
 * - onSend: () => void      (optional)
 */
export const QuoteConsultDialog = ({ quote, onClose, onEdit, onConvert, onPdf, onSend }) => {
  if (!quote) return null;
  const isAvenant = !!quote.is_avenant;
  return (
    <div
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 20 }}
      onClick={onClose}
      data-testid="quote-consult-overlay"
    >
      <div className="card" style={{ width: '100%', maxWidth: '960px', maxHeight: '92vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()} data-testid="quote-consult-dialog">
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
              {isAvenant ? 'Avenant' : 'Devis'} {quote.number}
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '0.84rem', color: '#5E5F66' }}>
              {quote.date}{quote.client_name ? ` - ${quote.client_name}` : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {onPdf && (
              <button className="btn btn-secondary btn-sm" onClick={onPdf} data-testid="qcd-pdf-btn" title="Voir le PDF">
                <FilePdf size={14} /> PDF
              </button>
            )}
            {onSend && (
              <button className="btn btn-secondary btn-sm" onClick={onSend} data-testid="qcd-send-btn" title="Envoyer au client par email">
                <PaperPlaneTilt size={14} /> Envoyer
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={onEdit} data-testid="qcd-edit-btn" title="Modifier ce devis">
              <PencilSimple size={14} /> Modifier
            </button>
            {onConvert && (
              <button className="btn btn-primary btn-sm" onClick={onConvert} data-testid="qcd-facturer-btn" title="Creer une facture avec un % d'avancement par article">
                <ArrowsClockwise size={14} /> Facturer
              </button>
            )}
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }} data-testid="qcd-close-btn" title="Fermer">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="stats-grid" style={{ padding: '14px 20px', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, borderBottom: '1px solid #F0F1F3' }}>
          <div className="stat-card"><div className="stat-label">Total HT</div><div className="stat-value" style={{ fontSize: '1.3rem' }}>{(quote.total_ht || 0).toLocaleString('fr-FR')} EUR</div></div>
          <div className="stat-card"><div className="stat-label">TVA</div><div className="stat-value" style={{ fontSize: '1.3rem', color: '#EF6C00' }}>{(quote.total_tva || 0).toLocaleString('fr-FR')} EUR</div></div>
          <div className="stat-card"><div className="stat-label">Total TTC</div><div className="stat-value" style={{ fontSize: '1.3rem', color: '#2E7D32' }}>{(quote.total_ttc || 0).toLocaleString('fr-FR')} EUR</div></div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" data-testid="qcd-lines-table">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ textAlign: 'center' }}>Unite</th>
                <th style={{ textAlign: 'right' }}>Prix unit.</th>
                <th style={{ textAlign: 'right' }}>Qte</th>
                <th style={{ textAlign: 'right' }}>Prix HT</th>
                <th style={{ textAlign: 'center' }}>TVA %</th>
                <th style={{ textAlign: 'right' }}>Prix TTC</th>
              </tr>
            </thead>
            <tbody>
              {(quote.lines || []).map((line, idx) => (
                <tr key={line.id || idx}>
                  <td style={{ fontWeight: 600 }}>{line.description}</td>
                  <td style={{ textAlign: 'center' }}>{line.unit}</td>
                  <td style={{ textAlign: 'right' }}>{Number(line.unit_price || 0).toLocaleString('fr-FR')} EUR</td>
                  <td style={{ textAlign: 'right' }}>{line.quantity}</td>
                  <td style={{ textAlign: 'right' }}>{Number(line.price_ht || 0).toLocaleString('fr-FR')} EUR</td>
                  <td style={{ textAlign: 'center' }}>{line.tva_rate}%</td>
                  <td style={{ textAlign: 'right' }}>{Number(line.price_ttc || 0).toLocaleString('fr-FR')} EUR</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#F4F5F7', fontWeight: 700 }}>
                <td colSpan={4} style={{ textAlign: 'right' }}>TOTAL</td>
                <td style={{ textAlign: 'right' }}>{(quote.total_ht || 0).toLocaleString('fr-FR')} EUR</td>
                <td></td>
                <td style={{ textAlign: 'right', color: '#2E7D32' }}>{(quote.total_ttc || 0).toLocaleString('fr-FR')} EUR</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {quote.notes && (
          <div style={{ margin: '14px 20px', padding: '10px 14px', backgroundColor: '#F4F5F7', borderRadius: 4, fontSize: '0.88rem', color: '#5E5F66' }}>
            {quote.notes}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteConsultDialog;
